import { Highlight, Prism, type Language } from 'prism-react-renderer';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import copy2clipboard from 'copy-to-clipboard';

import { CopyButton } from '@carbon/react';
import { useIntl } from 'react-intl';

import cn from 'clsx';
import * as css from './InlineCode.module.scss';

export type CodeProps = {
    lang?: string;
    src: string;

    copy?: boolean;
    light?: boolean;
    autoSelect?: boolean;
};

export default function InlineCode({ src, lang, copy, light, autoSelect = true }: CodeProps) {
    const ref = useRef<HTMLSpanElement>(null);
    const { formatMessage } = useIntl();

    const handleClick = useCallback(() => {
        if (!autoSelect) return;

        const selection = window.getSelection();
        const element = ref.current;
        if (selection && element) selection.setBaseAndExtent(element, 0, element, 1);
    }, [autoSelect]);

    // Prism ref getter is wrapped into "useEffect" so that we don't break SSR
    const [prism, setPrism] = useState(Prism);
    useEffect(() => {
        const g = typeof global !== 'undefined' ? global : window;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        setPrism(g.Prism || Prism);
    }, []);

    const code = src.replace(/\n+/, ' ').trim();

    return (
        <Highlight
            prism={prism}
            code={code}
            language={(lang || 'plain') as Language}
            children={({ className, style, tokens, getTokenProps, getLineProps }) => {
                return (
                    <span
                        className={cn(css.root, light && css.light)}
                        ref={ref}
                        onClick={autoSelect ? handleClick : undefined}
                    >
                        <code
                            className={cn(css.code, className)}
                            style={style}
                            children={tokens.map((line, i) => (
                                <span
                                    {...getLineProps({ line, key: i })}
                                    children={line.map((token, key) => (
                                        <span {...getTokenProps({ token, key })} />
                                    ))}
                                />
                            ))}
                        />

                        {copy === true && (
                            <CopyButton
                                align="left"
                                className={css.copyButton}
                                iconDescription={formatMessage({ defaultMessage: 'Copy to clipboard' })}
                                feedback={formatMessage({ defaultMessage: 'Copied!' })}
                                onClick={() => copy2clipboard(code)}
                            />
                        )}
                    </span>
                );
            }}
        />
    );
}
export const Bash = (props: Omit<CodeProps, 'lang'>) => <InlineCode lang="bash" {...props} />;
